import { CloudSDK } from '@sitecore-cloudsdk/core/browser';
import '@sitecore-cloudsdk/events/browser';
import '@sitecore-cloudsdk/search/browser';
import { getEnabledPackageBrowser as getEnabledPackage } from '@sitecore-cloudsdk/core/internal';
import scConfig from 'sitecore.config';

const SEARCH_PACKAGE = '@sitecore-cloudsdk/search';

export type CloudSdkBrowserSearchSettings = {
  sitecoreEdgeContextId: string;
  siteName: string;
  sitecoreEdgeUrl?: string;
};

let initPromise: Promise<void> | null = null;

async function awaitSearchPackageInit(): Promise<void> {
  const initState = getEnabledPackage(SEARCH_PACKAGE)?.initState;
  if (!initState) {
    throw new Error(
      'Cloud SDK search package is not registered. Ensure events and search browser modules are imported before initialize().'
    );
  }
  await initState;
}

/**
 * Browser `getWidgetData` requires Cloud SDK + events + search packages initialized once per tab.
 * Call from client bootstrap or before any `@sitecore-cloudsdk/search/browser` request.
 *
 * `enableBrowserCookie` must be true so the SDK can create/read the `sc_{contextId}` cookie
 * that holds `browser_id` — Search event APIs reject payloads without it.
 */
export function ensureCloudSdkBrowserForSearch(
  settings?: Partial<CloudSdkBrowserSearchSettings>
): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const edge = scConfig.api?.edge as { contextId?: string; clientContextId?: string } | undefined;
  const sitecoreEdgeContextId =
    settings?.sitecoreEdgeContextId?.trim() ||
    edge?.contextId?.trim() ||
    edge?.clientContextId?.trim() ||
    '';
  const siteName = settings?.siteName?.trim() || scConfig.defaultSite?.trim() || '';
  const sitecoreEdgeUrl =
    settings?.sitecoreEdgeUrl ||
    (typeof process !== 'undefined' &&
      (process.env.NEXT_PUBLIC_SITECORE_EDGE_URL || process.env.SITECORE_EDGE_URL)) ||
    undefined;

  if (!sitecoreEdgeContextId || !siteName) {
    return Promise.resolve();
  }

  if (!initPromise) {
    initPromise = (async () => {
      CloudSDK({
        sitecoreEdgeContextId,
        siteName,
        ...(sitecoreEdgeUrl ? { sitecoreEdgeUrl } : {}),
        enableBrowserCookie: true,
      })
        .addEvents()
        .addSearch()
        .initialize();
      await awaitSearchPackageInit();
    })().catch((err) => {
      initPromise = null;
      throw err;
    });
  }

  return initPromise;
}
